"use client";

import { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useAuth } from "@/components/providers/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { subscriptionTiers } from "@/lib/mock-data";
import { formatPrice } from "@/lib/utils";
import { EyeIcon, EyeOffIcon } from "lucide-react";

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  subscription: string;
}

export function SignUpForm() {
  const router = useRouter();
  const { signUp, signInWithGoogle, signInWithGithub, verifyRecaptcha } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState<FormData>({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    subscription: "free",
  });
  const [showPassword, setShowPassword] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError(""); // Clear errors when user types
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      // Form validation
      if (!formData.email.includes("@")) {
        throw new Error("Please enter a valid email address");
      }

      if (!formData.email || !formData.password) {
        throw new Error("Please provide both email and password");
      }

      if (!formData.firstName && !formData.lastName) {
        throw new Error("Please provide at least your first name or last name");
      }

      const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
      if (!passwordRegex.test(formData.password)) {
        throw new Error(
          "Password must be at least 8 characters and include uppercase, lowercase, number, and special character"
        );
      }

      // Verify with reCAPTCHA first
      await verifyRecaptcha('signup');

      // Sign up
      await signUp(formData);
      
      // Clear form and set loading false before redirect
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        subscription: "free",
      });
      setLoading(false);

      // Redirect to verification page explicitly on *successful* initial signup
      router.push("/verify-email");

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "An error occurred during sign up";
      // Check if the error is the specific one for pending verification
      if (errorMessage.startsWith("You have already started the sign-up process.")) {
        // Redirect to verification page with a flag to trigger auto-resend
        router.push("/verify-email?resend=true");
        // Optionally clear the form here too if desired
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          password: "",
          subscription: "free",
        });
      } else {
        // For all other errors, display them on the form
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setError("");
      setLoading(true);
      
      // Verify with reCAPTCHA first
      await verifyRecaptcha('signup_google');
      
      await signInWithGoogle();
      router.push("/");
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred during Google sign in");
      setLoading(false);
    }
  };

  return (
    <div className="grid gap-4">
      
      <form onSubmit={handleSubmit}>
        <div className="grid gap-4">
          <div className="grid gap-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  name="firstName"
                  placeholder="John"
                  type="text"
                  autoCapitalize="words"
                  autoComplete="given-name"
                  autoCorrect="off"
                  disabled={loading}
                  value={formData.firstName}
                  onChange={handleInputChange}
                />
              </div>
              <div>
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  name="lastName"
                  placeholder="Doe"
                  type="text"
                  autoCapitalize="words"
                  autoComplete="family-name"
                  autoCorrect="off"
                  disabled={loading}
                  value={formData.lastName}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  placeholder="name@example.com"
                  type="email"
                  autoCapitalize="none"
                  autoComplete="email"
                  autoCorrect="off"
                  disabled={loading}
                  value={formData.email}
                  onChange={handleInputChange}
                />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    disabled={loading}
                    value={formData.password}
                    onChange={handleInputChange}
                    className="pr-10" // Add padding for the button
                  />
                  <button
                    type="button" // Important: type="button" prevents form submission
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-muted rounded-md transition-colors"
                    tabIndex={-1} // Remove from tab sequence
                    disabled={loading}
                  >
                    {showPassword ? (
                      <EyeOffIcon className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <EyeIcon className="h-4 w-4 text-muted-foreground" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="grid gap-2">
            <Label>Subscription Plan</Label>
            <div className="grid gap-4 pt-2">
              {subscriptionTiers.map((tier) => (
                <div
                  key={tier.id}
                  className="flex items-center space-x-2 rounded-md border p-4 hover:border-blue-500 cursor-pointer transition-colors"
                >
                  <input
                    type="radio"
                    id={tier.id}
                    name="subscription"
                    value={tier.id}
                    className="h-4 w-4 border-primary text-primary focus:ring-primary"
                    checked={formData.subscription === tier.id}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                  <label
                    htmlFor={tier.id}
                    className="flex flex-1 cursor-pointer flex-col"
                  >
                    <span className="font-medium">{tier.name}</span>
                    <span className="text-sm text-muted-foreground">
                      {tier.description}
                    </span>
                    <span className="mt-1 font-medium">
                      {formatPrice(tier.price)}/month
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </div>
          {error && (
            <div className="text-sm text-destructive">
              {error}
            </div>
          )}
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? "Creating Account..." : "Create Account"}
          </Button>
        </div>
      </form>
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-background px-2 text-muted-foreground">
            Or continue with
          </span>
        </div>
      </div>
      <div className="flex justify-center gap-4">
        <div
          onClick={handleGoogleSignIn}
          className="hover:opacity-80 transition-opacity cursor-pointer"
        >
          <div className="w-10 h-10 relative flex items-center justify-center">
            <Image
              src="/images/google-logo.svg"
              alt="Sign up with Google"
              width={24}
              height={24}
              className="w-6 h-6"
            />
          </div>
        </div>
        <div
          onClick={async () => {
            try {
              setError("");
              setLoading(true);
              
              // Verify with reCAPTCHA first
              await verifyRecaptcha('signup_github');
              
              await signInWithGithub();
              router.push("/");
            } catch (err) {
              setError(err instanceof Error ? err.message : "An error occurred during GitHub sign in");
              setLoading(false);
            }
          }}
          className="hover:opacity-80 transition-opacity cursor-pointer"
        >
          <div className="w-10 h-10 relative flex items-center justify-center">
            <Image
              src="/images/github-logo.svg"
              alt="Sign up with GitHub"
              width={24}
              height={24}
              className="w-6 h-6 dark:invert"
            />
          </div>
        </div>
      </div>
      <div className="px-8 text-center text-sm text-muted-foreground mb-10">
        Already have an account?{" "}
        <Link
          href="/login"
          className="font-medium text-primary underline-offset-4 hover:underline"
        >
          Sign in
        </Link>
      </div>
    </div>
  );
}
